import React, { type FC, useEffect, useRef, useState } from 'react';

import { logEvent } from '../../lib/analytics';
import type { VoiceDemoDataType } from '../../lib/types';
import { useVoiceDemo } from '../../providers/VoiceDemoContext';
import { ChevronDown } from '../icons/ChevronDown';
import {
  countLanguages,
  getCountryNameFromLanguageCode,
} from '../../lib/utils';

const FlagIcon = ({ languageCode }: { languageCode: string }) => {
  const countryCode = languageCode.split('-')[1] || languageCode;
  const flag = countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(0x1f1e6 - 65 + char.charCodeAt(0)))
    .join('');

  return (
    <div className="text-[1rem]">
      <span className="-p-2 h-1">{flag}</span>
    </div>
  );
};

const VoiceOption: FC<
  VoiceDemoDataType & {
    handleUpdateLanguage: (languageCode: string) => void;
    showCountryName: boolean;
  }
> = (language) => {
  return (
    <button
      className="flex items-center gap-2 px-4 py-3 text-xs font-medium hover:bg-[#F5F5FA]"
      data-active={true}
      onClick={() => language.handleUpdateLanguage(language.languageCode)}>
      <FlagIcon languageCode={language.languageCode} />
      <div className="min-w-0 whitespace-nowrap text-[1rem]">
        {language.languageName}{' '}
        {language.showCountryName
          ? '(' + getCountryNameFromLanguageCode(language.languageCode) + ')'
          : ''}
      </div>
    </button>
  );
};

interface Props {
  allLanguages: VoiceDemoDataType[];
  onChange?: (v: string) => void;
  triggerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const LanguageSelector: FC<Props> = ({ allLanguages }) => {
  const { currentLanguage, handleUpdateLanguage } = useVoiceDemo();
  const currentLanguageCode = currentLanguage.languageCode;
  const availableLanguages = allLanguages.filter(
    (v) => v.languageCode !== currentLanguageCode,
  );
  const language = allLanguages.find(
    (v) => v.languageCode === currentLanguageCode,
  );
  const languageCount = countLanguages(
    availableLanguages.map((l) => l.languageCode),
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <div className="relative py-1" ref={dropdownRef}>
      {language && (
        <button
          key={language.languageName}
          className="flex cursor-pointer items-center gap-2 rounded-2xl bg-[#F5F5FA] px-[16px] py-[14px] leading-5 hover:border-[#e4eaf1] hover:bg-[#e4eaf1]"
          onClick={() => {
            logEvent('language_drowpdown_clicked', {
              isOpen: isDropdownOpen,
            });
            toggleDropdown();
          }}>
          <FlagIcon languageCode={currentLanguageCode} />

          <div className="text-[1rem]">{language.languageName}</div>
          <ChevronDown />
        </button>
      )}
      {isDropdownOpen && (
        <div className="dropdown-shadow absolute bottom-[60px] z-50 mt-1 flex h-[320px] flex-col overflow-scroll rounded border border-[#E9EAF0] bg-white no-scrollbar">
          {availableLanguages.map((v) => (
            <VoiceOption
              key={v.languageCode}
              {...v}
              showCountryName={languageCount[v.languageCode.split('-')[0]] > 1}
              handleUpdateLanguage={(lang) => {
                logEvent('language_updated', {
                  name: lang,
                });
                setIsDropdownOpen(false);
                handleUpdateLanguage(lang);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
